import Attrs from '../models/Attrs'
import renderOptionSelector from './OptionSelector'
import { generateComponentClasses } from '../styles/selectors'

import renderChatMessages from './ChatMessages'
import renderPlainText from './PlainText'
import renderCustomHTML from './CustomHTML'
import renderRichTextMarkdown from './RichTextMarkdown'
import renderCustomButton from './CustomButton'
import renderPaypalCheckout from './PaypalCheckout'
import renderInputBox from './InputBox'
import renderMediaImage from './MediaImage'
import renderMediaEmbed from './MediaEmbed'
import renderLottie from './Lottie'
import renderRive from './Rive'
import renderProgressBar from './ProgressBar'
import renderStripeCheckout from './StripeCheckout'
import renderStripeCheckout2 from './StripeCheckout2'
import renderBookMeeting from './BookMeeting'
import renderFileUpload from './FileUpload'
import renderChart from './Chart'
import { JSON_UPDATE_CONTROLLER, CONDITIONAL_TAGS_CONTROLLER } from '../helpers/controllers'
import type {
  ChatMessages,
  BookMeeting,
  ChildFlow,
  Component,
  Container,
  CustomButton,
  FileUpload,
  Flow,
  FlowPage,
  InputBox,
  Lottie,
  Rive,
  MediaEmbed,
  MediaImage,
  OptionSelector,
  OptionSelectorButton,
  PaypalCheckout,
  ProgressBar,
  StripeCheckout,
  StripeCheckout2,
  Chart,
  BaseBuilderVersion,
} from '../models/types'

import renderPageHeadingLabel from './deprecated/PageHeadingLabel'
import renderPageSubtitle from './deprecated/PageSubtitle'
import renderPageTitle from './deprecated/PageTitle'
import renderChildFlow from './ChildFlow'
import { getBaseBuilderVersion } from '../helpers/utils'
import { LoggerWithChild } from '../helpers/logger/types'

type ComponentAncestryTree = {
  component: Component
  children?: ComponentAncestryTree[]
}

const REPEATABLE_CONTROLLER = 'repeatable'
export function constructComponent(
  c: Component,
  page: FlowPage,
  flow: Flow,
  groupId: string,
  projectId: string,
  globalLocation: string | null,
  editingMode: boolean = false,
  liteEditingMode: boolean = false,
  allowJsonUpdate: boolean = false,
  static_render: boolean = false,
  children?: ComponentAncestryTree[],
  userData: { [key: string]: unknown } = {},
  logger?: LoggerWithChild
): string {
  if (c.hide) return ''

  // I need to check if the component doesn't have an ancestor that is hidden, in that case I will return an empty string
  let ancestors = []
  let components = globalLocation ? flow.components : page.components
  let checkingComponent = c

  //* In builder v4, the parent_key is deprecated and only the parent_id is used
  const builderVersion = getBaseBuilderVersion(flow.builder_version)

  while (
    builderVersion > 3
      ? checkingComponent.parent_id
      : checkingComponent.parent_key || checkingComponent.parent_id
  ) {
    const parent = components?.find((comp) =>
      builderVersion > 3
        ? comp.id === checkingComponent.parent_id
        : comp.key === checkingComponent.parent_key || comp.id === checkingComponent.parent_id
    )
    if (!parent) break
    ancestors.push(parent)
    checkingComponent = parent
  }
  if (ancestors.some((a) => a.hide)) return ''

  const attrs = new Attrs(static_render)
    .addComponentControllerFragment(c, page)
    .addDOMClass([`${c.type}`, ...generateComponentClasses({ component: c, page, builderVersion })])

  //* Update values for attr with lang- prefix
  Object.keys(c).forEach((key) => {
    if (key.startsWith('lang-') && typeof (c as any)[key] === 'string') {
      const value = (c as any)[key].replace(/\"/g, '&quot;')
      attrs.add(key, value)
    }
  })

  //* Add conditional-tag controller if needed
  if (c.conditional_tags && c.conditional_tags.length) {
    attrs.addController(CONDITIONAL_TAGS_CONTROLLER)
  }

  if (c.page_reactive_classes) attrs.addReactiveElement()
  if (c.conditions) attrs.addConditions(c.conditions)
  if (globalLocation) {
    attrs.add('data-global-component', 'true')
    attrs.add('data-global-component-location', globalLocation)
  }
  if (c.repeater_key) {
    attrs.addController('repeatable')
    // attrs.addValue('repeatable', `url`, `${context.origin}/${context.flowId}`)
    attrs.addValue('repeatable', 'page-id', page.id)
    attrs.addValue('repeatable', 'component-id', c.id)
    attrs.addValue('repeatable', 'component', JSON.stringify(c).replace(/"/g, '&quot;'))
    attrs.addValue('repeatable', 'repeater-key', c.repeater_key)
    attrs.addAction(`${REPEATABLE_CONTROLLER}:actions->flow#dispatchAction`)
  }
  if (c.is_repeatable_child) attrs.addValue('repeatable', 'child', 'true')

  if (editingMode || liteEditingMode || allowJsonUpdate)
    attrs
      .addController(JSON_UPDATE_CONTROLLER)
      .addValue(JSON_UPDATE_CONTROLLER, 'type', `component`)
      .addValue(JSON_UPDATE_CONTROLLER, 'group-id', groupId)
      .addValue(
        JSON_UPDATE_CONTROLLER,
        'path',
        globalLocation ? `components.${c.id}` : `pages.${page.id}.components.${c.id}`
      )
      .addValue(JSON_UPDATE_CONTROLLER, 'page-id', page.id)
      .addValue(JSON_UPDATE_CONTROLLER, 'component-id', c.id)
      .addClass(JSON_UPDATE_CONTROLLER, 'loading', 'loading')
  // @TODO: Add Flow ID in here
  // .addValue(JSON_UPDATE_CONTROLLER, 'flow-id', context.flowId)

  if (c.element_id) attrs.add('id', c.element_id)

  if (c.ignore_in_qa_tests) attrs.add('data-ignore-in-qa-tests', 'true')

  // @TODO: Make sure this merges in OK with any other styles
  if (c.__hide_initially) {
    attrs.add('style', 'display: none;')
    delete c.__hide_initially
  }

  //* Function to clean embed codes for static rendering
  function processEmbedCode(embedHTML: string) {
    if (embedHTML) {
      //* If I have <iframe>..</iframe> in my embed code string I want to remove it with everything inside it
      const regexIframe = /<iframe.*?>.*?<\/iframe>/g
      embedHTML = embedHTML.replace(regexIframe, '')

      //* Also I will remove autoplay from any tag
      const regexAutoPlay = /autoplay/g
      return embedHTML.replace(regexAutoPlay, '')
    }
    return embedHTML
  }

  switch (c.type) {
    case 'Container':
      return renderContainer(
        c as Container,
        page,
        flow,
        groupId,
        projectId,
        attrs,
        globalLocation,
        editingMode,
        liteEditingMode,
        allowJsonUpdate,
        static_render,
        userData,
        children
      )
    case 'FileUpload':
      return renderFileUpload(c as FileUpload, attrs, page, flow, editingMode, userData)
    case 'BookMeeting':
      return renderBookMeeting(c as BookMeeting, attrs)
    case 'ProgressBar':
      return renderProgressBar(c as ProgressBar, attrs, page, flow)
    case 'Lottie':
      return renderLottie(c as Lottie, attrs)
    case 'Rive':
      return renderRive(c as Rive, attrs)
    case 'PaypalCheckout':
      return renderPaypalCheckout(c as PaypalCheckout, attrs)
    case 'StripeCheckout':
      return renderStripeCheckout(c as StripeCheckout, attrs, page, flow, userData)
    case 'StripeCheckout2':
      return renderStripeCheckout2(c as StripeCheckout2, attrs, page, flow, userData)
    case 'CustomButton': {
      let component = JSON.parse(JSON.stringify(c)) as CustomButton
      if (static_render) {
        if (component.text) component.text = processEmbedCode(component.text)
        if (component.description)
          component.description = processEmbedCode(component.description || '')
      }
      return renderCustomButton(component as CustomButton, attrs, page, flow, editingMode, userData)
    }
    case 'ChatMessages':
      return renderChatMessages(c as ChatMessages, attrs, page, flow, editingMode)
    case 'CustomHTML':
    case 'RichText': {
      let component = JSON.parse(JSON.stringify(c))
      if (static_render && component.text) component.text = processEmbedCode(component.text)
      return renderCustomHTML(component, attrs, userData)
    }
    case 'RichTextMarkdown':
      return renderRichTextMarkdown(c, attrs, userData)
    case 'PlainText': {
      return renderPlainText(c, attrs, editingMode, userData)
      // attrs.addController('template')
      // return `<div ${attrs.toString()}>${c.text}</div>`
    }
    case 'MediaEmbed': {
      let component = JSON.parse(JSON.stringify(c)) as MediaEmbed
      if (static_render) {
        if (component.embed_code_raw)
          component.embed_code_raw = processEmbedCode(component.embed_code_raw || '')
        if (component.embed_code)
          component.embed_code = processEmbedCode(component.embed_code || '')
      }
      return renderMediaEmbed(component as MediaEmbed, attrs, page, flow, userData)
    }
    case 'MediaImage':
      return renderMediaImage(c as MediaImage, attrs, page, flow, userData)
    case 'InputBox':
      return renderInputBox(c as InputBox, attrs, page, flow, editingMode, userData)
    case 'OptionSelector': {
      let component = JSON.parse(JSON.stringify(c)) as OptionSelector
      if (static_render) {
        component.buttons?.forEach((b: OptionSelectorButton) => {
          if (b.text) b.text = processEmbedCode(b.text)
          if (b.description) b.description = processEmbedCode(b.description || '')
        })
      }
      return renderOptionSelector(component, attrs, page, flow, editingMode, userData)
    }
    case 'ChildFlow':
      return renderChildFlow(c as ChildFlow, attrs, page, flow, editingMode)

    // Deprecated:
    case 'PageTitle':
      // @ts-ignore: Deprecated
      return renderPageTitle(c, attrs, page)
    case 'PageSubtitle':
      // @ts-ignore: Deprecated
      return renderPageSubtitle(c, attrs, page)
    case 'PageHeadingLabel':
      // @ts-ignore: Deprecated
      return renderPageHeadingLabel(c, attrs, page)
    case 'Chart':
      return renderChart(c as Chart, attrs)

    default:
      return `<div ${attrs.toString()}>${c.text || c.label || `<${c.type}>`}</div>`
  }
}

export function buildComponentAncestryTree(
  components: Component[],
  builderVersion: BaseBuilderVersion
): ComponentAncestryTree[] {
  const CONTAINER = 'Container'
  const containers = components.filter((c) => c.type === CONTAINER)
  const seenContainers = new Set(containers.map((c) => [c.id, c.key]).flat())

  const componentsInContainers = new Set(
    components
      .filter((c) => {
        //* In builder v4, the parent_key is deprecated and only the parent_id is used
        const parentRef = builderVersion > 3 ? c.parent_id : c.parent_key || c.parent_id

        return parentRef ? seenContainers.has(parentRef || '') : false
      })
      .map((c) => c.id)
  )

  const seenComponents = new Set<string>()
  const componentMapper = (component: Component): ComponentAncestryTree => {
    const isAlreadyUsed = seenComponents.has(component.id)
    seenComponents.add(component.id)
    if (component.type === CONTAINER && !isAlreadyUsed) {
      const children = components
        .filter((c) => {
          //* In builder v4, the parent_key is deprecated and only the parent_id is used
          return builderVersion > 3
            ? c.parent_id === component.id
            : c.parent_key === component.key || c.parent_id === component.id
        })
        .map((child) => componentMapper(child))
      return { component, children }
    }
    return { component }
  }

  return components.filter((c) => !componentsInContainers.has(c.id)).map((c) => componentMapper(c))
}

export function ancestryTreeToHTML(
  tree: ComponentAncestryTree[],
  page: FlowPage,
  flow: Flow,
  groupId: string,
  projectId: string,
  globalLocation: string | null,
  editingMode: boolean,
  liteEditingMode: boolean,
  allowJsonUpdate: boolean,
  static_render: boolean,
  userData: { [key: string]: unknown },
  logger?: LoggerWithChild
): string {
  return tree
    .map((c) =>
      constructComponent(
        c.component,
        page,
        flow,
        groupId,
        projectId,
        globalLocation,
        editingMode,
        liteEditingMode,
        allowJsonUpdate,
        static_render,
        c.children,
        userData,
        logger
      )
    )
    .join('\n')
}

function renderContainer(
  c: Container,
  page: FlowPage,
  flow: Flow,
  groupId: string,
  projectId: string,
  attrs: Attrs,
  globalLocation: string | null,
  editingMode: boolean,
  liteEditingMode: boolean,
  allowJsonUpdate: boolean,
  static_render: boolean,
  userData: { [key: string]: unknown },
  children?: ComponentAncestryTree[]
): string {
  const descendants =
    children?.map((child) =>
      constructComponent(
        child.component,
        page,
        flow,
        groupId,
        projectId,
        globalLocation,
        editingMode,
        liteEditingMode,
        allowJsonUpdate,
        static_render,
        child.children,
        userData
      )
    ) || []
  attrs.addDOMClass(['Container', `ContainerType-${c.container_type || 'container'}`])
  return `<div ${attrs.toString()}>${descendants.join('\n')}</div>`
}
